import React from 'react';
import { Link } from 'react-router-dom';
import DoorlyLogo from './DoorlyLogo';
import { isNativeApp } from '../utils/isNativeApp';

export const LINKEDIN_URL = 'https://www.linkedin.com/company/godoorly';

// footnotes: the page's footnotes for its "*" claims, printed in the disclaimer block (never hidden at any width).
function Footer({ footnotes = [] }) {
  const currentYear = new Date().getFullYear();
  // 🔑 The installed app shows borrower links only (Jesse, 2026-10-19): Partners (/doorlypro) and Investors (/market)
  // are other apps' pages. The web keeps them.
  const native = isNativeApp();

  return (
    <footer className="bg-white border-t border-gray-200 text-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <DoorlyLogo size={36} />
              <span className="text-black font-semibold text-xl tracking-tight">Doorly</span>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed max-w-sm">
              Doorly makes home financing simple, fast, and transparent.
              Apply online, see every number, and close with confidence.
            </p>
            <div className="mt-4 flex space-x-3">
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-50 transition-colors">
                <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </div>
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" aria-label="Doorly on LinkedIn"
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-50 transition-colors">
                <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-black font-semibold text-sm uppercase tracking-wider mb-4">
              Quick Links
            </h3>
            <ul className="space-y-2">
              <li>
                <a href="https://www.godoorly.com" className="text-gray-600 hover:text-doorly-blue text-sm transition-colors duration-200">
                  Home
                </a>
              </li>
              <li>
                <Link to="/pre-qualify" className="text-gray-600 hover:text-doorly-blue text-sm transition-colors duration-200">
                  Apply Now
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-gray-600 hover:text-doorly-blue text-sm transition-colors duration-200">
                  Log In
                </Link>
              </li>
              {!native && (
                <>
                  <li>
                    <Link to="/doorlypro" className="text-gray-600 hover:text-doorly-blue text-sm transition-colors duration-200">
                      Partners
                    </Link>
                  </li>
                  <li>
                    <Link to="/market" className="text-gray-600 hover:text-doorly-blue text-sm transition-colors duration-200">
                      Investors
                    </Link>
                  </li>
                </>
              )}
              <li>
                <a href="https://www.godoorly.com/contact" className="text-gray-600 hover:text-doorly-blue text-sm transition-colors duration-200">
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h3 className="text-black font-semibold text-sm uppercase tracking-wider mb-4">
              Hours
            </h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start space-x-2">
                <svg className="w-4 h-4 text-doorly-blue mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Mon–Fri, 8am–5pm Central</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 mt-10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-500 text-xs">
                Go Doorly, LLC | NMLS #2764749 | Equal Housing Opportunity
              </p>
              <p className="text-gray-400 text-xs mt-1">
                &copy; {currentYear} Go Doorly, LLC. All rights reserved.
              </p>
            </div>
            {/* ⚠️ THESE WERE <span>s WITH cursor-pointer AND A HOVER STATE — they
                looked exactly like links and went nowhere. A dead "Privacy Policy"
                control is worse than none: it tells a visitor the policy exists and
                then refuses to show it. /privacy has been published all along and
                was reachable only from OfferAcceptedReview and a Step 4 surface.
                "Licenses" is dropped rather than linked to a page that does not
                exist — the same reason it should not have been a span. */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500">
              <Link to="/privacy" className="hover:text-black transition-colors underline">Privacy Policy</Link>
              <span className="text-gray-300">|</span>
              <Link to="/terms" className="hover:text-black transition-colors underline">Terms of Service</Link>
              <span className="text-gray-300">|</span>
              <Link to="/delete-account" className="hover:text-black transition-colors underline">Delete Account</Link>
            </div>
          </div>
          <p className="text-gray-400 text-xs mt-4 text-center md:text-left leading-relaxed">
            Equal Housing Opportunity. Go Doorly, LLC | NMLS #2764749. All loan programs and availability are subject to
            change without notice. Rates are subject to change. All applications are subject to underwriting
            guidelines and approval. Not all applicants will qualify.
          </p>
          {footnotes.map((f) => (
            <p key={f} data-footnote className="text-gray-400 text-xs mt-2 text-center md:text-left leading-relaxed">{f}</p>
          ))}
        </div>
      </div>
    </footer>
  );
}

export default Footer;
